import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Building2, Mail, Phone, MapPin, Hash, User, UserCheck, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import { useData } from '../contexts/DataContext';
import { Company } from '../types';

const CompaniesList: React.FC = () => {
  const { currentCompany, companies: allCompanies } = useTenant();
  const { plans, loadPlans } = useData();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    email: '',
    phone: '',
    address: '',
    legal_representative: '',
    legal_representative_rut: '',
    technical_contact: '',
    technical_email: '',
    contact_phone: '',
    plan_id: ''
  });

  // Verificar que solo Licitatrak pueda acceder
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';

  useEffect(() => {
    if (isSuperAdmin) {
      loadCompaniesWithPlan();
      loadPlans(); // Ensure plans are loaded
    }
  }, [isSuperAdmin]);

  const loadCompaniesWithPlan = async () => {
    try {
      console.log('Loading companies with plan information...');
      setIsLoading(true);
      let companiesToSet: Company[] = [];
      
      const { data, error } = await supabase
        .from('companies_with_plan')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading companies_with_plan view:', error);
        
        // Fallback: load companies directly and join with plans manually
        const { data: companiesData, error: companiesError } = await supabase
          .from('companies')
          .select(`
            *,
            plans (
              id,
              name,
              currency,
              value,
              description,
              features,
              is_active
            )
          `)
          .order('created_at', { ascending: false });
          
        if (companiesError) {
          console.error('Error loading companies with plans:', companiesError);
          // Final fallback: use companies from TenantContext
          if (allCompanies && allCompanies.length > 0) {
            companiesToSet = allCompanies;
            console.log('Using companies from TenantContext:', allCompanies.length);
          } else {
            setError('Error al cargar empresas');
            return;
          }
        } else {
          // Map the joined data
          companiesToSet = companiesData?.map(company => ({
            ...company,
            plan_name: company.plans?.name || null,
            plan_currency: company.plans?.currency || null,
            plan_value: company.plans?.value || null,
            plan_description: company.plans?.description || null,
            plan_features: company.plans?.features || null,
            plan_is_active: company.plans?.is_active || null
          })) || [];
          
          console.log('Loaded companies with manual join:', companiesToSet.length);
        }
      } else {
        // Map the view data to Company format
        companiesToSet = data?.map(item => ({
          id: item.id,
          name: item.name,
          rut: item.rut,
          email: item.email,
          phone: item.phone,
          address: item.address,
          legal_representative: item.legal_representative,
          legal_representative_rut: item.legal_representative_rut,
          technical_contact: item.technical_contact,
          technical_email: item.technical_email,
          contact_phone: item.contact_phone,
          plan_id: item.plan_id,
          created_at: item.created_at,
          updated_at: item.updated_at,
          // Additional plan info for display
          plan_name: item.plan_name,
          plan_currency: item.plan_currency,
          plan_value: item.plan_value,
          plan_description: item.plan_description,
          plan_features: item.plan_features,
          plan_is_active: item.plan_is_active
        })) || [];
        
        console.log('Loaded companies from view:', companiesToSet.length);
      }
      
      setCompanies(companiesToSet);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.rut && company.rut.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatRut = (value: string) => {
    const cleanValue = value.replace(/[^0-9kK]/g, '');
    if (cleanValue.length === 0) return '';
    
    const body = cleanValue.slice(0, -1);
    const dv = cleanValue.slice(-1);
    
    if (body.length === 0) return cleanValue;
    
    const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formattedBody}-${dv}`;
  };

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setFormData({ ...formData, rut: formatted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      setError('Solo Licitatrak puede gestionar empresas');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (editingCompany) {
        const { error } = await supabase
          .from('companies')
          .update(formData)
          .eq('id', editingCompany.id);

        if (error) throw error;
        console.log('Company updated successfully');
      } else {
        const { error } = await supabase
          .from('companies')
          .insert([formData]);

        if (error) throw error;
        console.log('Company created successfully');
      }

      await loadCompaniesWithPlan();
      resetForm();
    } catch (error: any) {
      console.error('Error saving company:', error);
      setError(error.message || 'Error al guardar empresa');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      rut: company.rut || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      legal_representative: company.legal_representative || '',
      legal_representative_rut: company.legal_representative_rut || '',
      technical_contact: company.technical_contact || '',
      technical_email: company.technical_email || '',
      contact_phone: company.contact_phone || '',
      plan_id: company.plan_id || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (company: Company) => {
    if (company.rut === '99.999.999-9') {
      setError('No se puede eliminar la empresa Licitatrak');
      return;
    }

    if (window.confirm(`¿Estás seguro de que deseas eliminar la empresa ${company.name}?`)) {
      try {
        const { error } = await supabase
          .from('companies')
          .delete()
          .eq('id', company.id);

        if (error) throw error;
        await loadCompaniesWithPlan();
      } catch (error: any) {
        console.error('Error deleting company:', error);
        setError(error.message || 'Error al eliminar empresa');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rut: '',
      email: '',
      phone: '',
      address: '',
      legal_representative: '',
      legal_representative_rut: '',
      technical_contact: '',
      technical_email: '',
      contact_phone: '',
      plan_id: ''
    });
    setEditingCompany(null);
    setShowForm(false);
    setError('');
  };

  const formatCurrency = (value: number, currency: string) => {
    return `${currency} ${value.toLocaleString()}`;
  };

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Denegado</h3>
        <p className="text-gray-600">Solo Licitatrak puede acceder a la gestión de empresas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Empresas</h1>
          <p className="text-gray-600">Administra todas las empresas del sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nueva Empresa
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Información de la Empresa */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  Información de la Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nombre de la empresa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RUT *
                    </label>
                    <input
                      type="text"
                      value={formData.rut}
                      onChange={handleRutChange}
                      required
                      disabled={!!editingCompany}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      placeholder="12.345.678-9"
                      maxLength={12}
                    />
                    {editingCompany && (
                      <p className="text-xs text-gray-500 mt-1">El RUT no se puede modificar</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="contacto@empresa.cl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+56 2 2345 6789"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dirección
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Dirección de la empresa"
                    />
                  </div>
                </div>
              </div>

              {/* Representante Legal */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Representante Legal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={formData.legal_representative}
                      onChange={(e) => setFormData({ ...formData, legal_representative: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: Juan Pérez Silva"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RUT
                    </label>
                    <input
                      type="text"
                      value={formData.legal_representative_rut}
                      onChange={(e) => setFormData({ ...formData, legal_representative_rut: formatRut(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="12.345.678-9"
                      maxLength={12}
                    />
                  </div>
                </div>
              </div>

              {/* Contacto Técnico */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <UserCheck className="h-5 w-5 mr-2" />
                  Contacto Técnico
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={formData.technical_contact}
                      onChange={(e) => setFormData({ ...formData, technical_contact: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: María González"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono de Contacto
                    </label>
                    <input
                      type="text"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+56 9 1234 5678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Técnico
                    </label>
                    <input
                      type="email"
                      value={formData.technical_email}
                      onChange={(e) => setFormData({ ...formData, technical_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="tecnico@empresa.cl"
                    />
                  </div>
                </div>
              </div>

              {/* Plan Contratado */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <Crown className="h-5 w-5 mr-2" />
                  Plan Contratado
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Plan
                    </label>
                    <select
                      value={formData.plan_id}
                      onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Seleccionar plan</option>
                      {plans.filter(p => p.is_active).map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {formatCurrency(plan.value, plan.currency)}
                        </option>
                      ))}
                    </select>
                    {plans.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        No se pudieron cargar los planes. Por favor, recarga la página.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Mensual
                    </label>
                    <input
                      type="text"
                      value={plans.find(p => p.id === formData.plan_id) ? formatCurrency(plans.find(p => p.id === formData.plan_id)!.value, plans.find(p => p.id === formData.plan_id)!.currency) : ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                      placeholder="Seleccione un plan"
                    />
                    {plans.find(p => p.id === formData.plan_id) && (
                      <p className="text-xs text-gray-500 mt-1">
                        {plans.find(p => p.id === formData.plan_id)!.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Guardando...' : editingCompany ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar empresas por nombre o RUT..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.length > 0 ? (
          filteredCompanies.map((company) => (
            <div key={company.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className={`p-2 rounded-lg mr-3 ${
                      (company as any).plan_name === 'Premium' ? 'bg-purple-100' :
                      (company as any).plan_name === 'Empresarial' ? 'bg-blue-100' :
                      (company as any).plan_name === 'Profesional' ? 'bg-green-100' :
                      'bg-gray-100'
                    }`}>
                      <Building2 className={`h-6 w-6 ${
                        (company as any).plan_name === 'Premium' ? 'text-purple-600' :
                        (company as any).plan_name === 'Empresarial' ? 'text-blue-600' :
                        (company as any).plan_name === 'Profesional' ? 'text-green-600' :
                        'text-gray-600'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {company.name}
                        {company.rut === '99.999.999-9' && (
                          <span className="text-sm text-purple-600 ml-2">(Super Admin)</span>
                        )}
                      </h3>
                      <div className="flex items-center text-sm text-gray-600">
                        <Hash className="h-3 w-3 mr-1" />
                        <span>{company.rut}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(company)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {company.rut !== '99.999.999-9' && (
                      <button
                        onClick={() => handleDelete(company)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  {company.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2" />
                      <span className="truncate">{company.email}</span>
                    </div>
                  )}
                  {company.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2" />
                      <span>{company.phone}</span>
                    </div>
                  )}
                  {company.address && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span className="truncate">{company.address}</span>
                    </div>
                  )}
                  {company.legal_representative && (
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 mr-2" />
                      <span className="truncate">{company.legal_representative}</span>
                    </div>
                  )}
                  {company.technical_contact && (
                    <div className="flex items-center text-sm text-gray-600">
                      <UserCheck className="h-4 w-4 mr-2" />
                      <span className="truncate">{company.technical_contact}</span>
                    </div>
                  )}
                  {company.contact_phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2 text-green-600" />
                      <span>{company.contact_phone}</span>
                    </div>
                  )}
                  {company.technical_email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-green-600" />
                      <span className="truncate">{company.technical_email}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Plan:</span>
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          (company as any).plan_name === 'Premium' ? 'bg-purple-100 text-purple-800' :
                          (company as any).plan_name === 'Empresarial' ? 'bg-blue-100 text-blue-800' :
                          (company as any).plan_name === 'Profesional' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <Crown className="h-3 w-3 mr-1" />
                        {(company as any).plan_name || 'Sin plan'}
                      </span>
                    </div>
                    {(company as any).plan_value && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Valor:</span>
                        <span className="text-xs font-medium text-green-600">
                          {formatCurrency((company as any).plan_value, (company as any).plan_currency || 'CLP')}/mes
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    Creada: {new Date(company.created_at).toLocaleDateString('es-ES')}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresas</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'No se encontraron empresas con los filtros aplicados.' : 'Comienza agregando una nueva empresa.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Nueva Empresa
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompaniesList;